"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Home, Search, Store, Tag, Tags, User } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import useFixedBarHeight, { FOOTER_NAV_HEIGHT_VAR } from "@/hooks/useFixedBarHeight";
import { BLOG_HOME_URL } from "@/helpers/blogUrl";
import { FOOTER_NAV_BAR_CLASS, footer_nav_item_class } from "../footerNavStyles";

/**
 * دکمه‌ی «دسته‌بندی» صفحه‌ی جدایی ندارد و همان منوی کشویی هدر بلاگ را باز
 * می‌کند؛ state آن منو داخل `HeaderBlog` است، پس ارتباط با رویداد window است.
 * هدر با رویداد TOGGLE منو را باز/بسته می‌کند و وضعیت جدید را با CHANGE اعلام
 * می‌کند تا آیکون این نوار هم فعال شود.
 */
export const BLOG_CATEGORIES_TOGGLE_EVENT = "jmt:blog-categories-toggle";
export const BLOG_CATEGORIES_CHANGE_EVENT = "jmt:blog-categories-change";

export const FooterIcons = {
    home: {
        fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H15.5V16H8.5V22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20L3 9Z" fill="#171717" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,
        outline: <Home size={24} />,
    },

    category: {
        fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 6C2.55228 6 3 6.44772 3 7L3 13.1719L3.00488 13.2705C3.02764 13.4994 3.12884 13.7148 3.29297 13.8789L10.0059 20.5918C10.2481 20.8359 10.5718 20.9822 10.915 21.0029C11.2584 21.0237 11.5973 20.9173 11.8672 20.7041C12.3005 20.3619 12.9292 20.4359 13.2715 20.8691C13.6138 21.3026 13.5399 21.9312 13.1064 22.2734C12.4509 22.7911 11.6277 23.0495 10.7939 22.999C9.96156 22.9486 9.17684 22.5942 8.58887 22.0029L1.87891 15.293C1.31636 14.7305 1.00017 13.9674 1 13.1719L1 7C1 6.44772 1.44772 6 2 6ZM13.1719 1C13.9674 1.00017 14.7305 1.31636 15.293 1.87891L22 8.58594C22.3186 8.90197 22.5715 9.27812 22.7441 9.69238C22.9168 10.1067 23.0059 10.5512 23.0059 11C23.0059 11.4488 22.9168 11.8933 22.7441 12.3076C22.572 12.7207 22.3193 13.0947 22.002 13.4102L17.4111 18.0029C17.0957 18.3202 16.7207 18.572 16.3076 18.7441C15.8933 18.9168 15.4488 19.0059 15 19.0059C14.5512 19.0059 14.1067 18.9168 13.6924 18.7441C13.2793 18.572 12.9043 18.3202 12.5889 18.0029L5.87891 11.293C5.31636 10.7305 5.00017 9.96738 5 9.17188V3C5 2.46957 5.21087 1.96101 5.58594 1.58594C5.96101 1.21086 6.46957 1 7 1L13.1719 1ZM7 9.17188L7.00488 9.27051C7.02764 9.49945 7.12884 9.71482 7.29297 9.87891L14.0059 16.5918L14.1084 16.6846C14.2149 16.7726 14.3339 16.8451 14.4619 16.8984C14.6324 16.9694 14.8153 17.0059 15 17.0059C15.1847 17.0059 15.3676 16.9694 15.5381 16.8984C15.7087 16.8274 15.864 16.723 15.9941 16.5918L20.5918 11.9941L20.6846 11.8916C20.7726 11.7851 20.8451 11.6661 20.8984 11.5381C20.9694 11.3676 21.0059 11.1847 21.0059 11C21.0059 10.8153 20.9694 10.6324 20.8984 10.4619C20.8274 10.2913 20.723 10.136 20.5918 10.0059L13.8789 3.29297C13.7148 3.12884 13.4994 3.02764 13.2705 3.00488L13.1719 3L7 3V9.17188Z" fill="#171717" />
            <path d="M13.1719 3L13.2705 3.00488C13.4994 3.02764 13.7148 3.12884 13.8789 3.29297L20.5918 10.0059C20.723 10.136 20.8274 10.2913 20.8984 10.4619C20.9694 10.6324 21.0059 10.8153 21.0059 11C21.0059 11.1847 20.9694 11.3676 20.8984 11.5381C20.8451 11.6661 20.7726 11.7851 20.6846 11.8916L20.5918 11.9941L15.9941 16.5918C15.864 16.723 15.7087 16.8274 15.5381 16.8984C15.3676 16.9694 15.1847 17.0059 15 17.0059C14.8153 17.0059 14.6324 16.9694 14.4619 16.8984C14.3339 16.8451 14.2149 16.7726 14.1084 16.6846L14.0059 16.5918L7.29297 9.87891C7.12884 9.71482 7.02764 9.49945 7.00488 9.27051L7 9.17188V3L13.1719 3ZM10.3506 5.00781C9.59401 5.08444 9.00391 5.72321 9.00391 6.5C9.00394 7.3284 9.6755 8 10.5039 8C11.3322 7.99985 12.0039 7.32831 12.0039 6.5C12.0039 5.72343 11.4135 5.08474 10.6572 5.00781L10.5039 5L10.3506 5.00781Z" fill="#171717" />
        </svg>,
        outline: <Tags size={24} />,
    },

    search: {
        fill: <Search size={24} strokeWidth={2.75} />,
        outline: <Search size={24} />,
    },

    shop: {
        fill: <Store size={24} strokeWidth={2.75} />,
        outline: <Store size={24} />,
    },

    profile: {
        fill: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" fill="#171717" />
            <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21H19Z" fill="#171717" />
            <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21H19Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,
        outline: <User size={24} strokeWidth={2} />,
    },
};

const strip_trailing_slash = (path = "") => path.replace(/\/+$/, "") || "/";

const FooterNavigationBlog = () => {
    const barRef = useFixedBarHeight(FOOTER_NAV_HEIGHT_VAR);
    const pathname = strip_trailing_slash(usePathname() || "");
    const router = useRouter();
    const { is_auth } = useAuthContext();
    const [categoriesOpen, setCategoriesOpen] = useState(false);

    useEffect(() => {
        const handleChange = (e) => setCategoriesOpen(Boolean(e.detail?.open));
        window.addEventListener(BLOG_CATEGORIES_CHANGE_EVENT, handleChange);
        return () => window.removeEventListener(BLOG_CATEGORIES_CHANGE_EVENT, handleChange);
    }, []);

    const toggleCategories = () => {
        window.dispatchEvent(new CustomEvent(BLOG_CATEGORIES_TOGGLE_EVENT));
    };

    const handleAccountClick = () => {
        if (categoriesOpen) toggleCategories();
        if (is_auth) {
            router.push("/account/");
        } else {
            openAuthModal("/account/");
        }
    };

    // وقتی منوی دسته‌بندی باز است فقط همان آیتم فعال دیده شود
    const navigationItems = [
        { key: "home", title: "خانه", href: `${BLOG_HOME_URL}/`, active: !categoriesOpen && pathname === BLOG_HOME_URL },
        { key: "category", title: "دسته‌بندی", onClick: toggleCategories, active: categoriesOpen },
        { key: "search", title: "جستجو", href: `${BLOG_HOME_URL}/search/`, active: !categoriesOpen && pathname === `${BLOG_HOME_URL}/search` },
        { key: "shop", title: "فروشگاه", href: "/", active: false },
        { key: "profile", title: "پروفایل", onClick: handleAccountClick, active: !categoriesOpen && pathname.startsWith("/account") },
    ];

    return (
        <nav ref={barRef} aria-label="نویگیشن مجله" className={FOOTER_NAV_BAR_CLASS}>
            {navigationItems.map((item) => {
                const className = footer_nav_item_class(item.active);
                const icon = item.active ? FooterIcons[item.key].fill : FooterIcons[item.key].outline;

                if (item.onClick) {
                    return (
                        <button
                            key={item.key}
                            type="button"
                            onClick={item.onClick}
                            {...(item.key === "category" ? { "aria-expanded": categoriesOpen } : {})}
                            className={className}
                        >
                            {icon}
                            <span>{item.title}</span>
                        </button>
                    );
                }

                return (
                    <Link
                        key={item.key}
                        href={item.href}
                        aria-current={item.active ? "page" : undefined}
                        onClick={categoriesOpen ? toggleCategories : undefined}
                        className={className}
                    >
                        {icon}
                        <span>{item.title}</span>
                    </Link>
                );
            })}
        </nav>
    );
};

export default FooterNavigationBlog;
